import { appController } from "../stores/useAppStore";
import { useFixtureScore } from "../stores";
import { MATCH_ID, TOURNAMENT_ID } from "../config/tournament";
import { SoccerMatchStatus, VisitType } from "../../../types";

const GA_ID = import.meta.env.VITE_GA_ID;
const CLIENT_ID = import.meta.env.VITE_LL_CLIENT_ID;

type CustomData = {
  ll_profile_id?: string;
  custom_id?: string;
  isInitialized?: boolean;
};

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Unknown";
}

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent.toLowerCase();
  const isIpad =
    /ipad/.test(ua) || (navigator.maxTouchPoints > 1 && /macintosh/.test(ua));
  const isTablet = isIpad || /tablet|android(?!.*mobile)/.test(ua);
  const isMobile = /mobi|iphone|ipod|android.*mobile/.test(ua);
  if (isTablet) return "tablet";
  if (isMobile) return "mobile";
  return "desktop";
}

export function mapMatchStatusToState(
  status?: SoccerMatchStatus | null
): "live" | "upcoming" | "past" | "cancelled" | "postponed" | "suspended" {
  if (status === SoccerMatchStatus.PLAYED) return "past";
  if (status === SoccerMatchStatus.PLAYING) return "live";
  if (status === SoccerMatchStatus.FIXTURE) return "upcoming";
  if (status === SoccerMatchStatus.CANCELLED) return "cancelled";
  if (status === SoccerMatchStatus.POSTPONED) return "postponed";
  if (status === SoccerMatchStatus.SUSPENDED) return "suspended";

  return "past";
}

export const getTabName = (pathName: string) => {
  if (pathName.includes("/fixture/lineup")) return "lineup";
  if (pathName.includes("/fixture/box-score")) return "box_score";
  if (pathName.includes("/fixture/stats")) return "stats";
  if (pathName.includes("/fixture/timeline")) return "timeline";
  if (pathName.includes("/fixture/standings")) return "standings";
  if (pathName.includes("/fixture/fixtures-list")) return "fixtures_list";

  return "";
};

function getCurrentTabType() {
  const path = window.location.pathname;
  return getTabName(path);
}

function getIsNewUser(): boolean {
  try {
    const profile = appController.getState("profile");
    const isFirstVisit =
      JSON.parse(profile?.custom_data || "{}").userVisitType ===
      VisitType.FirstVisit;
    return !!isFirstVisit;
  } catch {
    return false;
  }
}

function getDefaultEventProps() {
  const profile = appController.getState("profile");
  const fixtureState = useFixtureScore.getState();
  const currentMatchId =
    fixtureState.match?.id ||
    MATCH_ID ||
    appController.getState("matchData")?.id ||
    "";
  const matchStatus =
    appController.getState("matchData")?.status ||
    fixtureState.matchStatus ||
    null;

  const ll_username =
    // @ts-expect-error: tolerate various profile shapes
    profile?.display_name || profile?.nickname || profile?.name || "";
  const ll_user_id =
    // @ts-expect-error: tolerate various profile shapes
    profile?.id || profile?.user_id || "";

  return {
    is_new_user: getIsNewUser(),
    ll_username,
    ll_user_id,
    device_type: getDeviceType(),
    browser: getBrowser(),
    match_id: currentMatchId,
    tournament_id: TOURNAMENT_ID || "",
    match_state: mapMatchStatusToState(matchStatus),
    tab_type: getCurrentTabType(),
  } as Record<string, string | number | boolean | undefined>;
}

export class AnalyticsService {
  private static GA_ID = GA_ID;
  private static isInitialized = false;
  private static programId: string = "";
  private static customData: CustomData = {};

  private static readonly CUSTOM_DIMENSION_MAP = {
    dimension1: "page_name",
    dimension2: "client_id",
    dimension3: "is_new_user",
    dimension4: "device_type",
    dimension5: "team_id",
    dimension6: "team_name",
    dimension7: "is_journey_final_step",
    dimension8: "widget_id",
    dimension9: "widget_type",
    dimension10: "question_name",
    dimension11: "option_count",
    dimension12: "option_submitted",
    dimension13: "option_submitted_name",
    dimension14: "duration",
    dimension15: "source",
    dimension16: "dismiss_type",
    dimension17: "time_visible",
    dimension18: "email",
    dimension19: "name",
    dimension20: "success",
    dimension21: "error_type",
    dimension22: "is_correct",
    dimension23: "program_id",
    dimension24: "user_type",
    dimension25: "ll_client_id",
    dimension26: "ll_profile_id",
    dimension27: "custom_id",
    dimension28: "isInitialized",
  };

  private static getCommonDimensions() {
    return {
      ll_client_id: CLIENT_ID,
      ll_profile_id: this.customData?.ll_profile_id || "",
      custom_id: this.customData?.custom_id || "",
      isInitialized: this.customData?.isInitialized ?? true,
    };
  }

  static initialize(customData?: CustomData) {
    if (customData) {
      this.customData = customData;
    }

    if (!this.GA_ID) {
      console.error("GA_ID is not defined in environment variables");
      return;
    }

    if (!this.isInitialized) {
      // Create and append GTM script
      const script = document.createElement("script");
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_ID}`;
      document.body.append(script);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };

      window.gtag("js", new Date());
      window.gtag("config", this.GA_ID, {
        send_page_view: false,
        debug_mode: true,
        cookie_flags: "SameSite=None; Secure",
        user_id: this.customData?.ll_profile_id || "",
        ...this.getCommonDimensions(),
        custom_map: this.CUSTOM_DIMENSION_MAP,
      });

      window.gtag("event", "page_view", {
        user_id: this.customData?.ll_profile_id || "",
        ...this.getCommonDimensions(),
        send_to: this.GA_ID,
      });

      const messageHandler = (event: MessageEvent) => {
        this.handleIframeMessage(event);
      };

      window.addEventListener("message", messageHandler);
      this.isInitialized = true;
    } else {
      // Script already loaded — update config with new profile data
      window.gtag("config", this.GA_ID, {
        send_page_view: false,
        cookie_flags: "SameSite=None; Secure",
        user_id: this.customData?.ll_profile_id || "",
        ...this.getCommonDimensions(),
        custom_map: this.CUSTOM_DIMENSION_MAP,
      });
    }
  }

  private static handleIframeMessage(event: MessageEvent) {
    try {
      const data = event.data;
      if (data.type === "ANALYTICS_EVENT") {
        this.trackEvent(data.payload);
      }
    } catch (error) {
      console.error("Error processing iframe message:", error);
    }
  }

  static updateProgramId(programId: string) {
    this.programId = programId;
  }

  static trackEvent({
    event,
    properties,
  }: {
    event: string;
    properties: Record<string, string | number | boolean>;
  }) {
    if (!this.GA_ID) return;

    if (!this.isInitialized) {
      this.initialize();
    }

    const defaultProps = getDefaultEventProps();
    const commonDimensions = this.getCommonDimensions();
    const enhancedProperties = {
      event_category: properties.event_category || "iframe_interaction",
      send_to: this.GA_ID,
      org_name: "LFP",
      client_id: CLIENT_ID,
      program_id: this.programId,
      user_id: this.customData?.ll_profile_id || "",
      ...commonDimensions,
      ...defaultProps,
      ...properties,
    };

    window.gtag("event", event, {
      ...enhancedProperties,
      client_id: properties.client_id
        ? properties.client_id
        : enhancedProperties.client_id,
    });
  }
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export class SbsAnalyticsService {
  private static isInIframe = window.self !== window.top;

  static trackEvent(
    event: string,
    properties: Record<string, string | number>
  ) {
    const defaultProps = getDefaultEventProps();
    const eventProperty = {
      org_name: "LFP",
      client_id: CLIENT_ID,
      ...defaultProps,
      ...properties,
    } as Record<string, string | number>;

    AnalyticsService.trackEvent({
      event,
      properties: eventProperty,
    });
  }
}
